import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import { toast } from "react-toastify";
import api from "../../conf/api";
import { SocialPlatform } from "./Enums/enums";
import SocialFeedSchedulerBox from "./components/SocialFeedSchedulerBox";

const DAY_LABELS = {
  0: "Mon",
  1: "Tue",
  2: "Wed",
  3: "Thu",
  4: "Fri",
  5: "Sat",
  6: "Sun",
};

const PLATFORM_OPTIONS = [
  { value: "all", label: "All Platforms" },
  ...Object.values(SocialPlatform).map((v) => ({
    value: v,
    label: v.charAt(0) + v.slice(1).toLowerCase(),
  })),
];

function formatTime(utcTimeStr) {
  if (!utcTimeStr) return "—";
  const [h, m] = utcTimeStr.split(":");
  const d = new Date(Date.UTC(2000, 0, 1, parseInt(h), parseInt(m)));
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function SocialSchedulersPreview() {
  const [schedulers, setSchedulers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterPlatform, setFilterPlatform] = useState("all");
  const [filterFeed, setFilterFeed] = useState("all");
  const [feeds, setFeeds] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedScheduler, setSelectedScheduler] = useState(null);

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: null,
    confirmText: "",
  });

  // ── data loaders ─────────────────────────────────────────────────────────

  const fetchSchedulers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/mrss/schedulers/");
      setSchedulers(res.data?.results || res.data || []);
    } catch {
      toast.error("Failed to load social schedulers");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFeeds = useCallback(async () => {
    try {
      const res = await api.get("/mrss/feeds/", {
        params: { page: 1, page_size: 10000 },
      });
      setFeeds(res.data?.results || res.data || []);
    } catch {
      console.error("Error fetching feeds");
    }
  }, []);

  useEffect(() => {
    fetchSchedulers();
    fetchFeeds();
  }, [fetchSchedulers, fetchFeeds]);

  // ── actions ───────────────────────────────────────────────────────────────

  const handleAdd = () => {
    setSelectedScheduler(null);
    setDialogOpen(true);
  };

  const handleEdit = (row) => {
    setSelectedScheduler(row);
    setDialogOpen(true);
  };

  const handleDialogClose = (refresh) => {
    setDialogOpen(false);
    setSelectedScheduler(null);
    if (refresh) fetchSchedulers();
  };

  const handleDeleteClick = (id) => {
    setDeleteDialog({ open: true, id, confirmText: "" });
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/mrss/schedulers/${deleteDialog.id}/`);
      toast.success("Scheduler deleted");
      setDeleteDialog({ open: false, id: null, confirmText: "" });
      fetchSchedulers();
    } catch {
      toast.error("Failed to delete scheduler");
    }
  };

  // ── filtered rows ─────────────────────────────────────────────────────────

  const filteredRows = schedulers.filter((s) => {
    if (filterPlatform !== "all" && s.platform !== filterPlatform) return false;
    if (filterFeed !== "all" && String(s.feed) !== String(filterFeed)) return false;
    return true;
  });

  // ── columns ───────────────────────────────────────────────────────────────

  const columns = [
    {
      field: "feed_name",
      headerName: "Feed",
      flex: 1,
    },
    {
      field: "platform",
      headerName: "Platform",
      width: 140,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color="primary"
          variant="outlined"
        />
      ),
    },
    {
      field: "selected_days",
      headerName: "Days",
      flex: 1,
      renderCell: (params) => {
        const days = params.value || [];
        if (days.length === 0) return <Typography variant="caption">—</Typography>;
        return (
          <Box display="flex" gap={0.5} flexWrap="wrap">
            {[...days]
              .sort((a, b) => a - b)
              .map((d) => (
                <Chip
                  key={d}
                  label={DAY_LABELS[d]}
                  size="small"
                  variant="outlined"
                />
              ))}
          </Box>
        );
      },
    },
    {
      field: "time_start",
      headerName: "Time Windows",
      width: 220,
      renderCell: (params) => {
        const row = params.row;
        const windows = [[row.time_start, row.time_end]];
        if (row.time_start_2 && row.time_end_2) {
          windows.push([row.time_start_2, row.time_end_2]);
        }
        return (
          <Box display="flex" gap={0.5} flexWrap="wrap">
            {windows.map(([start, end], i) => (
              <Chip
                key={i}
                label={`${formatTime(start)}–${formatTime(end)}`}
                size="small"
                variant="outlined"
              />
            ))}
          </Box>
        );
      },
    },
    {
      field: "uploading_gap",
      headerName: "Gap (min)",
      width: 110,
      renderCell: (params) =>
        params.value != null ? `${params.value} min` : "—",
    },
    {
      field: "max_uploads_per_day",
      headerName: "Max/Day",
      width: 100,
      renderCell: (params) => (params.value != null ? params.value : "—"),
    },
    {
      field: "is_active",
      headerName: "Active",
      width: 90,
      renderCell: (params) => (
        <Chip
          label={params.value ? "Active" : "Inactive"}
          size="small"
          color={params.value ? "success" : "default"}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 110,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleEdit(params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteClick(params.row.id)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Social Feed Schedulers
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Configure publishing windows for each feed + platform combination
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchSchedulers}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<AddIcon />}
            onClick={handleAdd}
          >
            Add Scheduler
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Platform</InputLabel>
          <Select
            label="Platform"
            value={filterPlatform}
            onChange={(e) => setFilterPlatform(e.target.value)}
          >
            {PLATFORM_OPTIONS.map((p) => (
              <MenuItem key={p.value} value={p.value}>
                {p.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Feed</InputLabel>
          <Select
            label="Feed"
            value={filterFeed}
            onChange={(e) => setFilterFeed(e.target.value)}
          >
            <MenuItem value="all">All Feeds</MenuItem>
            {feeds.map((f) => (
              <MenuItem key={f.id} value={f.id}>
                {f.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      <Paper sx={{ height: 600, width: "100%" }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          loading={loading}
          disableSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          getRowHeight={() => "auto"}
          sx={{ "& .MuiDataGrid-cell": { py: 1 } }}
        />
      </Paper>

      {/* Create / Update dialog */}
      <SocialFeedSchedulerBox
        open={dialogOpen}
        scheduler={selectedScheduler}
        onClose={handleDialogClose}
      />

      {/* Delete confirmation */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null, confirmText: "" })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" mb={2}>
            This will permanently delete the scheduler. Type{" "}
            <b>delete</b> to confirm.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder='Type "delete" to confirm'
            value={deleteDialog.confirmText}
            onChange={(e) =>
              setDeleteDialog((prev) => ({ ...prev, confirmText: e.target.value }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setDeleteDialog({ open: false, id: null, confirmText: "" })
            }
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={deleteDialog.confirmText.toLowerCase() !== "delete"}
            onClick={handleDeleteConfirm}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
